import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { HttpRequestOptions, HttpServiceConstants } from '@kimaya/ngx-http-service';
import { AuthService, AuthServiceConstants } from '@kimaya/ngx-auth-service';
import { Observable, timer } from 'rxjs';
import { CookieStorage } from '@kimaya/ngx-cookie-storage';
import { EnvService } from './env.service';
import { COOKIES, CoreConstants, OrganisationService, PREFERENCE_TYPE, UserService } from './core';
import { LanguageService } from './core/language';
import { WebSocketConfigService } from './core/web-socket/web-socket-config.service';

@Injectable({
  providedIn: 'root',
})
export class AppConfigService {

  private authService: AuthService;
  private userService: UserService;
  private unAuthorizedError: boolean;
  private organisationService: OrganisationService;
  private cookieStorage: CookieStorage;
  private languageService: LanguageService;
  private env: EnvService;
  private webSocketConfigService: WebSocketConfigService;

  constructor(private httpClient: HttpClient, private injector: Injector) {
  }

  init(): Promise<any> {
    return new Promise((resolve: any): any => {
      this.authService = this.injector.get(AuthService);
      this.userService = this.injector.get(UserService);
      this.languageService = this.injector.get(LanguageService);
      this.webSocketConfigService = this.injector.get(WebSocketConfigService);
      this.env = this.injector.get(EnvService);
      this.organisationService = this.injector.get(OrganisationService);
      this.cookieStorage = this.injector.get(CookieStorage);
      HttpServiceConstants.serverUrl = this.getBaseUrl(this.env.isVM);
      HttpServiceConstants.headers = {app: this.env.app, domain: this.env.domain};
      AuthServiceConstants.headers = {app: this.env.app, domain: this.env.domain};
      CoreConstants.excludeDomainUrl = this.env.excludeDomainUrl;
      AuthServiceConstants.serverUrl = HttpServiceConstants.serverUrl;
      AuthServiceConstants.accessTokenKey = 'sseyrteyrt';
      AuthServiceConstants.refreshTokenKey = 'sheyrteyrt';
      AuthServiceConstants.headerToken = 'Basic cmVzdDpyZXN0';
      AuthServiceConstants.redirectPath = '/';
      AuthServiceConstants.login = '/oauth/token';
      AuthServiceConstants.logout = '/user/v1/logout';
      AuthServiceConstants.loggedInKey = 'ineyrteyrt';
      CoreConstants.appName = this.env.app;
      CoreConstants.domain = this.env.domain;
      CoreConstants.allowSocialLogin = this.env.allowSocialLogin;
      CoreConstants.allowedSocialLogin = this.env.socialLogin;
      CoreConstants.socketEnabled = this.env.socketEnabled;
      this.languageService.languageList = [{
        key: 'en',
        value: 'English'
      }, {
        key: 'de',
        value: 'German'
      }, {
        key: 'es',
        value: 'Spanish'
      }, {
        key: 'fr',
        value: 'French'
      }];
      this.languageService.languages = this.languageService.languageList.map(lang => lang.value);
      this.languageService.defaultLanguage = 'en';
      if (!this.env.encryptionIV) {
        console.error('Encryption Configuration Missing !!!');
      } else {
        CoreConstants.encryptionIV = this.env.encryptionIV;
      }
      if (this.env.firstPage) {
        CoreConstants.firstPage = this.env.firstPage;
        CoreConstants.firstPageOriginal = this.env.firstPage;
      }
      this.organisationService.setupSubDomain();
      if (this.authService.isLoggedIn()) {
        this.get(CoreConstants.API.userProfile).subscribe((response) => {
          this.webSocketConfigService.connect(() => {
            const preference = this.cookieStorage.get('preference');
            if (preference === PREFERENCE_TYPE.org && this.cookieStorage.get(COOKIES.organization)) {
              this.userService.setUser(response.userDetails as any, true, true);
              this.organisationService.organisationId = this.cookieStorage.get(COOKIES.organization);
              this.organisationService.setNavigation();
              resolve();
            } else if (preference === PREFERENCE_TYPE.internal) {
              this.userService.setUser(response.userDetails as any, true);
              this.organisationService.setNavigation(true);
              resolve();
            } else {
              this.organisationService.removeOrganisationId();
              this.organisationService.redirectToSelectOrganisation();
              this.userService.setUser(response.userDetails as any);
              resolve();
            }
          });

        }, () => {
          this.authService.removeUsersDetailsAndRedirect();
          resolve();
        });
      } else {
        resolve();
      }
    });
  }


  getBaseUrl(isVM: boolean): string {
    if (isVM) {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port;
      return port ? `${protocol}//${hostname}:${port}/api` : `${protocol}//${hostname}/api`;
    }
    return this.env.serverUrl;
  }
  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description :get RequestOptions.
   * @param endPoint - http api end point without host
   * @param [params] - to set in param header
   * @param [body] - to set in body header
   * @param [contentType] - - to set in content type header
   * @param [backendUrl] - if you want to change host pass this
   */
  get(endPoint: string,
      params?: object,
      body?: object,
      contentType?: string,
      backendUrl?: string
  ): Observable<any> {
    return new Observable((resolver: any) => {
      let count = 0;
      const takeCallback = (): void => {

        this.httpClient.get(this.getFullUrl(endPoint, backendUrl), this.getRequestOptions(params, body, contentType))
          .subscribe(response => resolver.next(response), (httpError) => {
            count++;
            this.handleError(httpError).subscribe((handleError) => {
              if (handleError === 'unAuthorizedError') {
                this.checkToken().then(() => {
                  if (count >= 3) {
                    resolver.error(httpError);
                  } else {
                    takeCallback();
                  }
                });
              } else if (handleError === 'success') {
                if (count >= 3) {
                  resolver.error(httpError);
                } else {
                  takeCallback();
                }
              } else if (handleError === 'error') {
                resolver.error(httpError);
              }
            });
          });
      };

      takeCallback();
    });
  }


  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description : get token from cookie
   * @return string
   */
  private getTokenDetails(): string {
    if (!HttpServiceConstants.serverUrl) {
      throw new Error('please set serverUrl for HttpServiceConstants');
    }
    return this.authService.getAccessToken();
  }

  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description : check token if token get will return else continue 10 sec and return
   */
  private checkToken(): Promise<any> {
    return new Promise((resolve: any) => {
      let count = 0;
      const timerSub = timer(1000, 1000);
      const subscription = timerSub.subscribe(time => {
        count++;
        if (count > 5) {
          subscription.unsubscribe();
          resolve(true);
        }
        if (this.authService.getAccessToken()) {
          subscription.unsubscribe();
          resolve(true);
        }
      });
    });
  }

  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description :Build base API url.
   * @param url - end point of api
   * @param [backendUrl] - to hit api on another server
   * @return string
   */
  private getFullUrl(url: string, backendUrl?: string): string {
    if (backendUrl) {
      return backendUrl + url;
    }
    return HttpServiceConstants.serverUrl + url;
  }

  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description :get RequestOptions.
   * @param params - to set in param header
   * @param body - to set in body header
   * @param contentType - to set in content type header
   */
  private getRequestOptions(params?: any, body?: any, contentType?: string): HttpRequestOptions {
    const httpOptions: HttpRequestOptions = {};

    /*set header section*/
    const httpHeader = {
      Authorization: 'Bearer ' + this.getTokenDetails(),
    };

    /*Set Extra Headers from HttpServiceConstants*/
    if (Object.keys(HttpServiceConstants.headers || {}).length > 0) {
      for (const headerDataKey in HttpServiceConstants.headers) {
        if (HttpServiceConstants.headers.hasOwnProperty(headerDataKey)) {
          httpHeader[headerDataKey] = HttpServiceConstants.headers[headerDataKey];
        }
      }
    }

    /*set content type for multipart*/
    if (contentType) {
      httpHeader['Content-type'] = contentType;
    }

    httpOptions.headers = new HttpHeaders(httpHeader);

    /*Set param section*/
    if (params) {
      httpOptions.params = new HttpParams({fromObject: params});
    }

    /*Set param section*/
    if (body) {
      httpOptions.body = body;
    }
    return httpOptions;
  }

  /***
   * @Author : Ganeshram Kumhar (kumarganesh088@gmail.com)
   * @description : handle http errors, if invalid token or ( unAutorized)401 error occure then it will call refresh token
   * @param: error HttpErrorResponse
   */
  private handleError(error: HttpErrorResponse | any): Observable<string> {
    return new Observable((resolve) => {
      if (error.status === 401) {
        if (this.unAuthorizedError) {
          resolve.next('unAuthorizedError');
        } else {
          this.unAuthorizedError = true;
          this.authService.refreshToken()
            .subscribe((response) => {
              this.authService.storeAccessTokenResponse(response.access_token, response.expires_in, response.refresh_token);
              this.unAuthorizedError = false;
              resolve.next('success');
            }, (tokenError) => {
              this.authService.removeUsersDetailsAndRedirect();
              this.unAuthorizedError = false;
              resolve.next('error');
            });
        }
      } else {
        resolve.next('error');
      }
    });
  }
}
